import { createZodFormResolver } from '@mews/framework';
import { RateGroupWithPaymentPolicies } from '@mews-commander/data-access';
import { CommanderFormProps, useDialogForm, useHandledDialogFormControls } from '@mews-commander/form';
import { Stack } from '@optimus-web/core';
import { SettlementConfigurationFormValues, settlementConfigurationSchema } from '../../../schemas';
import { SettlementConfigurationsFieldName } from '../types';
import { SettlementConfigurationFields } from '../fields';
import { SettlementConfigurationFieldsProps } from '../fields/SettlementConfigurationFields';

export const useSettlementConfigurationEditDialogForm = ({
    title,
    ...params
}: SettlementConfigurationEditDialogFormProps & { title: React.ReactNode }) =>
    useDialogForm({
        id: 'settlementConfigurationEditForm',
        renderForm: (props) => <SettlementConfigurationEditDialogForm {...props} {...params} />,
        title,
    });

export type SettlementConfigurationEditDialogFormProps = Pick<
    RateGroupWithPaymentPolicies,
    'settlementMaximumTimeUnits' | 'settlementExtent' | 'options' | 'settlementCurrencyCode' | 'id' | 'enterpriseId'
> &
    Pick<SettlementConfigurationFieldsProps, 'currencyScope'> & {
        onSubmit: (formValues: SettlementConfigurationFormValues) => Promise<unknown>;
    };

const SettlementConfigurationEditDialogForm = ({
    settlementMaximumTimeUnits,
    settlementExtent,
    options,
    settlementCurrencyCode,
    id,
    enterpriseId,
    currencyScope,
    onSubmit,
    ...other
}: CommanderFormProps & SettlementConfigurationEditDialogFormProps) => {
    const settlementConfigurationFormResolver = createZodFormResolver(settlementConfigurationSchema);
    const { renderFormContent } = useHandledDialogFormControls<SettlementConfigurationFormValues>({
        ...other,
        values: {
            [SettlementConfigurationsFieldName.CurrencyCode]: settlementCurrencyCode,
            [SettlementConfigurationsFieldName.Extent]: settlementExtent,
            [SettlementConfigurationsFieldName.MaximumTime]: settlementMaximumTimeUnits,
            [SettlementConfigurationsFieldName.Options]: options,
        },
        onSubmit,
        resolver: settlementConfigurationFormResolver,
    });

    return renderFormContent(
        <Stack vertical spacing="300">
            <SettlementConfigurationFields
                currencyFieldIsDisabled
                enterpriseId={enterpriseId}
                currencyScope={currencyScope}
            />
        </Stack>
    );
};
